import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal, X } from 'lucide-react';

import { UnifiedTaskDetailsDialog } from '@/components/tasks/UnifiedTaskDetailsDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { TypeBadge } from '@/components/shared/TypeBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { taskService, TASK_TYPES, PHASE_STATUSES, PRIORITIES, type Task, type TaskListParams } from '@/services/task.service';

export function TasksPage() {
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);

  const params: TaskListParams = {
    cursor: cursors[page],
    limit: 25,
    sort: 'created_at',
    order: 'desc',
    ...(search && { search }),
    ...(typeFilter && { task_type: typeFilter }),
    ...(statusFilter && { phase_status: statusFilter }),
    ...(priorityFilter && { priority: priorityFilter }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['tasks', params],
    queryFn: () => taskService.list(params).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const tasks = data?.data ?? [];
  const pagination = data?.pagination;

  function resetPage() { setPage(0); setCursors([undefined]); }

  const hasFilters = !!typeFilter || !!statusFilter || !!priorityFilter;
  const activeFilterCount = [typeFilter, statusFilter, priorityFilter].filter(Boolean).length;

  function clearFilters() {
    setTypeFilter('');
    setStatusFilter('');
    setPriorityFilter('');
    resetPage();
  }

  const columns: Column<Task>[] = [
    {
      key: 'reference_id',
      header: 'ID',
      render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.reference_id}</span>,
      className: 'w-[100px]',
    },
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (r) => <span className="font-medium">{r.title}</span>,
    },
    {
      key: 'task_type',
      header: 'Type',
      render: (r) => <TypeBadge type={r.task_type} />,
    },
    {
      key: 'phase_status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.phase_status} />,
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (r) => <PriorityBadge priority={r.priority} />,
    },
    {
      key: 'assignee',
      header: 'Assignee',
      hideable: true,
      render: (r) => r.assignee ? (
        <div className="flex items-center gap-1.5">
          <UserAvatar name={r.assignee.full_name} avatarUrl={r.assignee.avatar_url} size="xs" />
          <span className="text-sm">{r.assignee.full_name}</span>
        </div>
      ) : <span className="text-sm text-muted-foreground">—</span>,
    },
    {
      key: 'end_date',
      header: 'Due',
      sortable: true,
      hideable: true,
      render: (r) => (
        <span className="text-sm text-muted-foreground">
          {r.end_date ? new Date(r.end_date).toLocaleDateString() : '—'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Tasks" description="All tasks across your workspace" />

      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search tasks..." value={search} onChange={(e) => { setSearch(e.target.value); resetPage(); }} className="pl-9" />
          </div>
          <Button
            variant={showFilters ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-1.5"
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {showFilters && (
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); resetPage(); }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
            >
              <option value="">All types</option>
              {TASK_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); resetPage(); }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
            >
              <option value="">All statuses</option>
              {PHASE_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => { setPriorityFilter(e.target.value); resetPage(); }}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm min-w-[160px] focus:outline-none focus:ring-1 focus:ring-brand-primary dark:bg-background dark:border-border"
            >
              <option value="">All priorities</option>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
            {hasFilters && (
              <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
                <X className="h-3 w-3" /> Clear filters
              </button>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          data={tasks}
          isLoading={isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => setViewTaskId(r.id)}
          emptyTitle="No tasks found"
          emptyDescription="Try adjusting your filters."
          pagination={{
            hasNext: !!pagination?.has_more,
            hasPrev: page > 0,
            onNext: () => {
              if (pagination?.cursor) {
                setCursors((p) => { const n = [...p]; n[page + 1] = pagination.cursor!; return n; });
                setPage((p) => p + 1);
              }
            },
            onPrev: () => setPage((p) => Math.max(0, p - 1)),
          }}
        />

        <UnifiedTaskDetailsDialog
          open={!!viewTaskId}
          onOpenChange={(v) => !v && setViewTaskId(null)}
          taskId={viewTaskId}
        />
      </div>
    </div>
  );
}
